import { c as _c } from "react/compiler-runtime";
import { useEffect, useMemo } from 'react';
import moment from 'moment';
import type { NetworkPersonFilter } from '@/components/pages/Home/NetworkOwnerFilter';
import type { NetworkTypeFilterOption } from '@/components/pages/Home/NetworkTypeFilter';
import type { InferenceAlgorithm } from '@/services/bayesianApi/models';
import type { NetworkStatus } from '@/services/models/enums';
import { showError } from '@/utils/toast';
import { trpc } from '@/utils/trpc';
const PAGE_SIZE = 50;
export type LoadedNetwork = {
  id: string;
  name: string;
  editedOn: string;
  editedOnExact: string;
  createdOn: string | undefined;
  createdOnExact: string | undefined;
  inferenceAlgorithm: InferenceAlgorithm;
  status: NetworkStatus;
  ownerName: string | undefined;
  lastEditor: string | undefined;
  marvinWritable: boolean;
  isMain: boolean;
  isRegularCopy: boolean;
  isReleaseBranch: boolean;
  /** True for a branch cut from a protected ref (KBE-793/789). */
  isRefBranch: boolean;
  version: string | null;
  forkedFromName: string | null;
  retiredAt: string | null;
};

/** Timestamp column the Network browser orders by. Mirrors the
 * server-side `NetworkSortColumn`. */
export type NetworkSortColumn = 'lastEdited' | 'created';
export type NetworkSortDirection = 'asc' | 'desc';
export type NetworkSort = {
  column: NetworkSortColumn;
  direction: NetworkSortDirection;
};
export const DEFAULT_NETWORK_SORT: NetworkSort = {
  column: 'lastEdited',
  direction: 'desc'
};
export type UseNetworksOptions = {
  enabled: boolean;
  search: string;
  /** Filter by creator (network.owner_id); `users` ids are profile ids. */
  ownerFilter: NetworkPersonFilter;
  /** Filter by last editor (history.author); `users` ids are emails. */
  editorFilter: NetworkPersonFilter;
  /** Filter by network "type" facet (Marvin / Released / Regular copy /
   * Branches). Empty means no type filtering. */
  typeFilter: ReadonlyArray<NetworkTypeFilterOption>;
  /** Active sort column + direction. Applied server-side. */
  sort: NetworkSort;
  /** When true, retired networks are included. Defaults to false. */
  includeRetired?: boolean;
};
export type UseNetworksResult = {
  networks: LoadedNetwork[];
  isLoading: boolean;
  hasNextPage: boolean;
  isFetchingNextPage: boolean;
  fetchNextPage: () => void;
};
export const useRefetch = () => {
  const utils = trpc.useUtils();
  return () => Promise.all([utils.network.getNetworksOrderedByLastModification.refetch(), utils.network.getNetworksWithLastEditorPage.refetch(), utils.network.listNetworkNamesForLists.refetch(), utils.network.listDistinctNetworkEditors.refetch(), utils.network.listDistinctNetworkOwners.refetch()]);
};

/**
 * Refresh the network lists without letting them decide anything.
 *
 * `useRefetch` is five refetches behind one `Promise.all`, each subject to the
 * global three-attempt retry with backoff — so it can take ~20s or reject
 * outright. Anything sequenced *after* an `await` on it inherits that, and for
 * create/duplicate what came after was `router.push` to the new network: a
 * rejected or slow refetch meant the researcher never landed there and the
 * header kept showing the network they came from, with no error and nothing to
 * recover it. That is one of the "the write landed but the UI never reflects
 * it" cases in KBE-770 — the duplicate returned 200 and the client then only
 * ever re-fetched the *original* network.
 *
 * The refresh is cosmetic anyway: `last_networks_update_timestamp` broadcasts
 * invalidate these same queries, and a broadcast missed while unsubscribed is
 * now reconciled on SUBSCRIBED (see `realtimeResync.ts`). So fire it and move
 * on.
 */
export const useBackgroundRefetch = () => {
  const $ = _c(2);
  const refetch = useRefetch();
  let t0;
  if ($[0] !== refetch) {
    t0 = () => {
      refetch().catch(_temp);
    };
    $[0] = refetch;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  return t0;
};
export const useNetworks = t0 => {
  const $ = _c(29);
  const {
    enabled,
    search,
    ownerFilter,
    editorFilter,
    typeFilter,
    sort,
    includeRetired
  } = t0;
  const t1 = search.length > 0 ? search : undefined;
  let t2;
  if ($[0] !== ownerFilter) {
    t2 = ownerFilter.kind === "users" ? {
      kind: "users",
      ownerIds: [...ownerFilter.ids]
    } : ownerFilter;
    $[0] = ownerFilter;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  let t3;
  if ($[2] !== editorFilter) {
    t3 = editorFilter.kind === "users" ? {
      kind: "users",
      emails: [...editorFilter.ids]
    } : editorFilter;
    $[2] = editorFilter;
    $[3] = t3;
  } else {
    t3 = $[3];
  }
  let t4;
  if ($[4] !== typeFilter) {
    t4 = typeFilter.length > 0 ? [...typeFilter] : undefined;
    $[4] = typeFilter;
    $[5] = t4;
  } else {
    t4 = $[5];
  }
  const t5 = includeRetired ?? false;
  let t6;
  if ($[6] !== sort.column || $[7] !== sort.direction || $[8] !== t1 || $[9] !== t2 || $[10] !== t3 || $[11] !== t4 || $[12] !== t5) {
    t6 = {
      limit: PAGE_SIZE,
      search: t1,
      sortBy: sort.column,
      sortDirection: sort.direction,
      ownerFilter: t2,
      editorFilter: t3,
      typeFilter: t4,
      includeRetired: t5
    };
    $[6] = sort.column;
    $[7] = sort.direction;
    $[8] = t1;
    $[9] = t2;
    $[10] = t3;
    $[11] = t4;
    $[12] = t5;
    $[13] = t6;
  } else {
    t6 = $[13];
  }
  let t7;
  if ($[14] !== enabled) {
    t7 = {
      enabled,
      getNextPageParam: _temp2,
      initialCursor: null
    };
    $[14] = enabled;
    $[15] = t7;
  } else {
    t7 = $[15];
  }
  const query = trpc.network.getNetworksWithLastEditorPage.useInfiniteQuery(t6, t7);
  let t8;
  let t9;
  if ($[16] !== query.error) {
    t8 = () => {
      if (query.error) {
        showError("Failed to load networks", {
          description: "Please check your internet connection and try again"
        });
      }
    };
    t9 = [query.error];
    $[16] = query.error;
    $[17] = t8;
    $[18] = t9;
  } else {
    t8 = $[17];
    t9 = $[18];
  }
  useEffect(t8, t9);
  let result;
  if ($[19] !== query.data?.pages) {
    const pages = query.data?.pages ?? [];
    result = [];
    for (const page of pages) {
      for (const n of page.items) {
        const editedMoment = moment(n.lastEditTime ?? n.lastModification);
        const createdMoment = n.createdAt ? moment(n.createdAt) : undefined;
        result.push({
          id: n.id,
          name: n.name,
          editedOn: editedMoment.fromNow(),
          editedOnExact: editedMoment.calendar(),
          createdOn: createdMoment?.fromNow(),
          createdOnExact: createdMoment?.calendar(),
          inferenceAlgorithm: n.inferenceAlgorithm,
          status: n.status,
          ownerName: n.ownerName ?? undefined,
          lastEditor: n.lastEditor ?? undefined,
          marvinWritable: n.marvinWritable,
          isMain: n.isMain,
          isRegularCopy: n.isRegularCopy,
          isReleaseBranch: n.isReleaseBranch,
          isRefBranch: n.isRefBranch,
          version: n.version,
          forkedFromName: n.forkedFromName,
          retiredAt: n.retiredAt ? String(n.retiredAt) : null
        });
      }
    }
    $[19] = query.data?.pages;
    $[20] = result;
  } else {
    result = $[20];
  }
  const networks = result;
  const t10 = query.hasNextPage ?? false;
  let t11;
  if ($[21] !== query) {
    t11 = () => {
      if (query.hasNextPage && !query.isFetchingNextPage) {
        query.fetchNextPage();
      }
    };
    $[21] = query;
    $[22] = t11;
  } else {
    t11 = $[22];
  }
  let t12;
  if ($[23] !== networks || $[24] !== query.isFetchingNextPage || $[25] !== query.isLoading || $[26] !== t10 || $[27] !== t11) {
    t12 = {
      networks,
      isLoading: query.isLoading,
      hasNextPage: t10,
      isFetchingNextPage: query.isFetchingNextPage,
      fetchNextPage: t11
    };
    $[23] = networks;
    $[24] = query.isFetchingNextPage;
    $[25] = query.isLoading;
    $[26] = t10;
    $[27] = t11;
    $[28] = t12;
  } else {
    t12 = $[28];
  }
  return t12;
};
export default useNetworks;
function _temp(error) {
  console.error("Refreshing the network lists failed", error);
}
function _temp2(lastPage) {
  return lastPage.nextCursor ?? undefined;
}